import { HTTP_OPTIONS } from '@silverfort-nodelibs/common/utils/consts';
import { httpGet } from '../../../../components/utils';
import { InsightDetailsResponse } from '../types/Insights';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Pageable } from '../../../../api/types/Pageable';
import { HttpResponse } from '../../../../api/types/Http';

export interface InsightEntitiesFilters {
    ruleName: string;
    daysDiff?: number;
    entitySourceType?: string;
    domainName?: string[];
    riskLevel?: number[];
    riskIndicator?: string[];
    entityType?: number[];
    entityName?: string;
    entityStatus?: string[];
    platforms?: string[];
    pageNumber?: number;
    pageSize?: number;
    sortField?: string;
    sortDirection?: string;
    activeOnly?: boolean;
}

const useGetInsightEntities = ({
    entityName,
    entityStatus,
    entityType,
    riskIndicator,
    riskLevel,
    domainName,
    entitySourceType,
    daysDiff,
    ruleName,
    platforms,
    pageNumber,
    pageSize,
    sortField,
    sortDirection,
    activeOnly
}: InsightEntitiesFilters) => {
    const [data, setData] = useState<Pageable<InsightDetailsResponse>>();
    const [isFetching, setIsFetching] = useState(false);
    const [hasNextPage, setHasNextPage] = useState(true);
    const [error, setError] = useState<string>();
    const lastRequestId = useRef(0);

    const filters = useMemo(() => {
        return {
            entityName,
            entityStatus,
            entityType,
            riskIndicator,
            riskLevel,
            domainName,
            entitySourceType,
            daysDiff,
            ruleName,
            platforms,
            pageNumber,
            pageSize,
            sortField,
            sortDirection,
            activeOnly
        };
    }, [
        entityName,
        entityStatus,
        entityType,
        riskIndicator,
        riskLevel,
        domainName,
        entitySourceType,
        daysDiff,
        ruleName,
        platforms,
        pageNumber,
        pageSize,
        sortField,
        sortDirection,
        activeOnly
    ]);

    const getInsightsEntities = useCallback(async (ruleName: string, requestParams: InsightEntitiesFilters) => {
        if (!ruleName) {
            return;
        }
        const myRequestId = lastRequestId.current;
        setIsFetching(true);
        try {
            const result = (await httpGet(`/insights/details/${ruleName}`, {
                [HTTP_OPTIONS.REQ_OPTIONS]: {
                    params: requestParams
                }
            })) as unknown as HttpResponse<InsightDetailsResponse>;

            const data = result.data;

            if (myRequestId !== lastRequestId.current) {
                return;
            }

            if (!!data?.insightsDetails?.length) {
                setData((prevState) => {
                    return prevState && filters.pageNumber !== 1
                        ? { ...prevState, pages: [...prevState.pages, data] }
                        : { pages: [data] };
                });
            } else {
                setHasNextPage(false);
            }
        } catch (err) {
            setError('Failed to get insights entities');
        }

        setIsFetching(false);
    }, []);

    const fetchNextPage = useCallback(async () => {
        if (hasNextPage && !!data?.pages?.length) {
            await getInsightsEntities(filters.ruleName, { ...filters, pageNumber: data.pages.length });
        }
    }, [filters, hasNextPage, data, getInsightsEntities]);

    const refreshTable = useCallback(() => {
        setData(undefined);
        setHasNextPage(true);
        setIsFetching(false);
        setError(undefined);
        lastRequestId.current++;
        getInsightsEntities(filters.ruleName, filters);
    }, [filters, getInsightsEntities]);

    useEffect(() => {
        refreshTable();
    }, [refreshTable]);

    const mergedPages: InsightDetailsResponse = useMemo(() => {
        return (
            data?.pages.reduce(
                (acc, page) => {
                    return {
                        insightsDetails: [...acc.insightsDetails, ...page.insightsDetails],
                        insightsDetailsAdded: [...acc.insightsDetailsAdded, ...page.insightsDetailsAdded]
                    };
                },
                { insightsDetails: [], insightsDetailsAdded: [] }
            ) ?? { insightsDetails: [], insightsDetailsAdded: [] }
        );
    }, [data]);

    return { data, isFetching, error, fetchNextPage, hasNextPage, mergedPages, refreshTable };
};

export default useGetInsightEntities;
